import {
    createUniversalFetch,
    navigateTo,
    useAuth,
    useLocale,
    useLocalePrefix,
    useLogger,
    useNuxtApp,
    useRuntimeConfig,
    type Language,
} from '#imports';
import type {LocalePrefixConfig} from '@malt/nuxt-i18n-module/types';
import {withForcedLocalePrefix} from '../utils/localeSelection';

const universalFetch = createUniversalFetch({baseURL: '/navbar'});

export function useLocalizationSelection() {
    const logger = useLogger();
    const nuxtApp = useNuxtApp();
    const {loggedIn, refresh} = useAuth();
    const {locale, isMultiLocaleHost} = useLocale();
    const runtimeConfig = useRuntimeConfig();
    const {getLocaleCodeFromPrefix, getLocalePrefix, withoutLocalePrefix} = useLocalePrefix();

    // Get the current locale from the URL or the browser's locale
    function getCurrentLocale(): string {
        const localePrefix = getLocalePrefix();
        const localeFromPrefix = localePrefix ? getLocaleCodeFromPrefix(localePrefix) : undefined;

        return localeFromPrefix ?? locale.value;
    }

    async function changeLanguageAndRedirect(selectedLanguage: Language) {
        if (!selectedLanguage.host) {
            return;
        }

        const targetUrl = _resolveTargetUrl(selectedLanguage.host, selectedLanguage.locale);
        const hostChanged = targetUrl.host !== window.location.host;

        if (loggedIn && hostChanged) {
            await _changeLanguageAndRedirectForLoggedInUser(targetUrl);
            return;
        }

        if (loggedIn && isMultiLocaleHost.value) {
            try {
                nuxtApp.$localeToSyncWithServer.value = selectedLanguage.locale.replaceAll('_', '-');
                await refresh();
            } catch (e) {
                logger.error("Can't change language", e);
                return;
            }
        }

        navigateTo(targetUrl.toString(), {external: true});
    }

    function _resolveTargetUrl(newHost: string, locale?: string): URL {
        const url = new URL(window.location.href);
        url.searchParams.delete('lg');

        const i18nConfig = runtimeConfig.public['@malt/nuxt-i18n-module'];
        const maybeLocalePrefix = locale?.replace('_', '-').toLowerCase(); // en_GB -> en-gb
        const isActiveLocalePrefix = (i18nConfig?.supportedLocalePrefixes as LocalePrefixConfig[] | undefined)?.some(
            ({prefix, redirectionActive}) => redirectionActive !== false && prefix === maybeLocalePrefix,
        );
        const shouldRedirectToMultiLocaleHost = locale && isActiveLocalePrefix;
        const strippedPathname = withoutLocalePrefix(url.pathname) as string;

        if (shouldRedirectToMultiLocaleHost && maybeLocalePrefix) {
            url.host = toComHost(newHost);
            url.pathname = appKeepsLocalePrefixForCurrentUser() ? withForcedLocalePrefix(strippedPathname, maybeLocalePrefix) : strippedPathname;
        } else {
            url.pathname = strippedPathname;
            url.host = newHost;
        }

        return url;
    }

    // Product apps (account, dashboards) are not mounted on /en-be/account. Prefix only when this app keeps it.
    function appKeepsLocalePrefixForCurrentUser(): boolean {
        const i18nConfig = runtimeConfig.public['@malt/nuxt-i18n-module'];
        if (!i18nConfig?.enableLocalePrefixStrategy) {
            return false;
        }
        return !(loggedIn && i18nConfig.stripLocalePrefixWhenLoggedIn);
    }

    // @XAuthTours(step = tour_send_redirect, includeCode = true)
    async function _changeLanguageAndRedirectForLoggedInUser(targetUrl: URL): Promise<void> {
        // lib-front-shared-domains/nuxt-navbar-module/src/runtime/logged/features/language-selector/language-selector.store.ts
        try {
            const userToken = await universalFetch<string>('/api/user/x-auth-token');
            await universalFetch('/api/user/unsudosu', {method: 'POST'});

            const xAuthUrl = new URL(`${window.location.protocol}//${targetUrl.host}/navbar/api/xauth`);
            xAuthUrl.searchParams.append('redirectUrl', targetUrl.toString());
            xAuthUrl.searchParams.append('x-auth', userToken);

            navigateTo(xAuthUrl.toString(), {external: true});
        } catch (e) {
            logger.error("Can't change language", e);
        }
    }

    // Locale sites use a subdomain (en.malt.be) but malt.com is only served from www.
    // deven.malt.fr → dev.malt.com, en.integration.malt.be → www.integration.malt.com.
    function toComHost(newHost: string): string {
        const parts = newHost.split('.');
        const subdomain = parts[0];
        if (subdomain?.startsWith('dev')) {
            parts[0] = 'dev';
        } else if (subdomain && subdomain !== 'www') {
            parts[0] = 'www';
        }
        parts[parts.length - 1] = 'com';
        return parts.join('.');
    }

    return {
        // functions A-Z
        changeLanguageAndRedirect,
        getCurrentLocale,
        // refs A-Z
    };
}
